/* eslint-disable react/jsx-closing-bracket-location */
import { Box } from '@material-ui/core';
import { Row } from 'components/layout/Row';
import React, {
  ChangeEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Column } from 'components/layout/Column';
import format from 'date-fns/format';
import { parseISO } from 'date-fns';
import { DEFAULT_GROUND_COLOR, PLAYER_MARKER_COLOR } from 'components/track/ground/constants';
import { CLUB, ClubTypeRes, ClubTypeVars } from 'query/club';
import { getMarkersColor, getPairedGroundColor, GroundType } from 'models/ground';
import { TourProvider } from '@reactour/tour';
import { FPS, maxSamplesPerFrame } from 'components/constants';
import StaffOnlyWrapper from 'components/StaffOnlyWrapper';
import { useLazyQueryCached, useQueryCached } from '../utils/graphql';
import { TeamSessionsType } from '../../models/team_session';
import {
  SESSION, TEAMSESSION_ATHLETES_SESSIONS, TeamSessionsVars, TeamSessionType,
} from '../../query/session';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import { CursorContext } from '../track/TrackChartContainer';
import PlayerControls from './PlayerControls';
import PlayersTable from './PlayersTable';
import ErrorGuard from '../layout/ErrorGuard';
import WebPlayerHeader from './WebPlayerHeader';
import Ground from './Ground';
import { webplayerWorkerNew } from '../../workers';
import {
  checkNearEndOfInterval, enterFullscreen, exitFullscreen, FpsCtrl,
} from './utils';
import {
  DrillTimesType,
  InfoPlayers,
  MarkDrill,
  PlayerDataInterval,
  PlayerDataIntervals,
  PlayersDetails,
  PlayersExtraData, PlayersTeams,
  PlayersTeamsContextValue,
  SeriesState,
  STATUS_TEXT,
  WebPlayerProps,
  WebPlayerWorkerResponse,
} from '../../types/webPlayer';
import { OptionType } from '../Autocomplete';
import TourGuide, { steps } from './TourGuide';
import { LPSFromGPSPath } from '../../lib/geoNew';
import { withMe } from '../MeUserWrapper';
import { MeType } from '../../models/me';
import { getLoadingTimeFrame } from '../../lib/webPlayer';
import { mergeArraysUnique } from '../../lib/array';
import { AthleteSessionType } from '../../models/athlete_session';

// @todo verifica se il timestamp del cursore va oltre il limite della traccia
// @todo dopo il nuovo loading il moltiplicatore sembra tornare a 1

const PLAYER_STATUS = {
  STOP: false,
  PLAY: true,
};

const LOADING_TYPE = {
  NONE: 0,
  BG: 1,
  FG: 2,
} as const;

type LoadingType = typeof LOADING_TYPE[keyof typeof LOADING_TYPE];

const timeFormat = 'H:mm:ss';
const valueLabelFormat = (value: number, _: number) => format(value, timeFormat);

export const PlayersTeamsContext = React.createContext<PlayersTeamsContextValue>({
  playersTeams: { asd: ['asdasd'] },
  setPlayersTeams: () => { console.log('default'); },
});

const keyboardEventHandler = (
  event: KeyboardEvent,
  keyCallbacks: {
    key: string,
    callbackFn: Function,
    ctrlKey?: boolean,
    altKey?: boolean,
    shiftKey?: boolean,
    metaKey?: boolean
  }[],
) => {
  const callback = keyCallbacks.find((k) => k.key === event.code
    && !!k.ctrlKey === event.ctrlKey
    && !!k.altKey === event.altKey
    && !!k.metaKey === event.metaKey
    && !!k.shiftKey === event.shiftKey);

  if (callback) {
    callback.callbackFn();
  }
};

const WebPlayer = (props: WebPlayerProps & { me: MeType }) => {
  const {
    currentDrill,
    defaultGround,
    me,
    isFullscreen,
    sessionGround,
    sessionId,
    showVertices,
    templateId,
    teamId,
  } = props;

  const [currentActivePlayer, setCurrentActivePlayer] = useState<null | string>(null);
  const [currentHoverPlayer, setCurrentHoverPlayer] = useState<null | string>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [enabledPlayers, setEnabledPlayers] = useState<Array<string>>([]);
  const [ground, setGround] = useState<GroundType | undefined>(sessionGround);
  const [intervals, setIntervals] = useState<PlayerDataIntervals>([]);
  const [loadingInterval, setLoadingInterval] = useState<PlayerDataInterval | null>(null);
  const [jerseyOrNumber, setJerseyOrNumber] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState<Array<string>>([]);
  const [marks, setMarks] = useState<MarkDrill[]>([]);
  const [playerSpeed, setPlayerSpeed] = useState(1); // ms tra i frame (1/25)
  const [playersColors, setPlayersColors] = useState<Record<string, string>>({});
  const [series, setSeries] = useState<SeriesState>({});
  const [status, setStatus] = useState(STATUS_TEXT.INIT);
  const [tickThrottle, setTickThrottle] = useState(false);
  const [timestamps, setTimestamps] = useState<number[]>([]);
  const [timestampsToAdd, setTimestampsToAdd] = useState<number[]>([]);
  const [trailsEnabled, setTrailsEnabled] = useState(false);
  const [workerIsReady, setWorkerIsReady] = useState(false);
  const [currentPlayerStatus, setCurrentPlayerStatus] = useState(false);
  const [infoPlayers, setInfoPlayers] = useState<InfoPlayers>([]);
  const [playersTeams, setPlayersTeams] = useState<PlayersTeams>({});
  const [playerLinks, setPlayerLinks] = useState<Set<[string, string]>>(new Set());
  const [playerFPS, setPlayerFPS] = useState(FPS);

  const workerCheckerRef = useRef<any>(null);
  const playerRef = React.useRef<null | HTMLDivElement>(null);
  const currDrillRef = useRef(1);
  const currStepRef = useRef(1);
  const currPlayerSpeedRef = useRef(1);
  const fpsCtrlRef = useRef<any>();
  const lastRequestedTimestampRef = useRef<number | null>(null);
  const lastLoadedTimestampRef = useRef<number | null>(null);
  const lastLoadedIntervalRef = useRef<PlayerDataInterval | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const prevPlayerIsLoadingRef = useRef<boolean>(false);
  const loadingTypeRef = useRef<LoadingType>(LOADING_TYPE.NONE);
  const prevLoadingStateRef = useRef(false);
  const idleLoadingIntervalRef = useRef<any>(null);
  const loadingPlayersRef = useRef<number>(0);

  // const jwtToken = (localStorage.getItem('exelio_token')) || undefined;
  const isLoadingForeground = loadingTypeRef.current === LOADING_TYPE.FG;
  const getJwtToken = () => (localStorage.getItem('exelio_token')) || undefined;

  const findNearTimestamp = useCallback(
    (ts: number, tss?: number[]) => (tss || timestamps).find((t) => (t - ts) < 500 && (t - ts) >= 0),
    [timestamps],
  );

  const findNearTimestampIndex = useCallback(
    (ts: number, tss?: number[]) => (tss || timestamps).findIndex((t) => Math.abs(t - ts) < 1500),
    [timestamps],
  );

  const handleCurrentTimeChange = useCallback((value: number) => {
    lastRequestedTimestampRef.current = value;
    setCurrentTime(value);
  }, []);

  const cursorValue = useMemo(() => ({
    cursor: findNearTimestamp(currentTime),
    setCursor: handleCurrentTimeChange,
  }), [currentTime, findNearTimestamp, handleCurrentTimeChange]);

  /* SESSION DATA */
  const {
    loading: sessionLoading,
    data,
  } = useQueryCached<{ res: TeamSessionType }, TeamSessionsVars>(SESSION, {
    variables: {
      drill: currentDrill,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
      withAthleteSession: true,
      withGround: true,
    },
    ...CACHE_AND_NETWORK,
    onCompleted: (result) => {
      const tracksGround = result?.res?.athleteSessions && result.res.athleteSessions.length > 0
        && result.res.athleteSessions[0] && result.res.athleteSessions[0].track?.ground;

      if (result?.res?.startTimestamp) {
        const timezoneOffset = -new Date(result.res.startTimestamp).getTimezoneOffset() * 60000;
        const newTimestamp = new Date(result.res.startTimestamp).getTime() + timezoneOffset;
        setCurrentTime(newTimestamp);
        lastRequestedTimestampRef.current = newTimestamp;
        setStatus(STATUS_TEXT.SET_START_TIME);
      }

      if (result?.res?.drills?.relatedDrills) {
        const drillsMarks: {
          value: number,
          label: string,
          index?: number,
          id?: number,
          tags?: string[],
        }[] = [];
        setStatus(STATUS_TEXT.DATA_SESSION_LOADED);

        result.res.drills.relatedDrills.forEach((drill) => {
          const timezoneOffset = drill?.start
            ? -new Date(drill.start).getTimezoneOffset() * 60000
            : 0;

          drillsMarks.push({
            value: drill ? (new Date(drill.start)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.start), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });

          // @todo fare in modo che il valore sia con indice e non timestamp
          drillsMarks.push({
            value: drill ? (new Date(drill.end)).getTime() + timezoneOffset : 0,
            label: drill ? `${format(parseISO(drill.end), timeFormat)}` : '---',
            index: drill?.index || undefined,
            id: drill?.id || undefined,
            tags: drill?.tags || undefined,
          });
        });

        setMarks(drillsMarks);
        setStatus(STATUS_TEXT.SET_DRILLS);
      }

      if (tracksGround) {
        setGround(tracksGround);
      }

      setStatus(STATUS_TEXT.PLAYERS_LOADING);
      // eslint-disable-next-line no-use-before-define
      loadSessions();
    },
  });

  const endSessionTimestampRef = useRef<number | null>(0);

  const endSessionTimestamp = useMemo(() => {
    if (data?.res?.endTimestamp) {
      return new Date(data.res.endTimestamp).getTime() - new Date(data.res.endTimestamp).getTimezoneOffset() * 60000;
    }

    return 0;
  }, [data?.res?.endTimestamp]);

  useEffect(() => {
    endSessionTimestampRef.current = endSessionTimestamp;
  }, [endSessionTimestamp]);

  /* SESSION DATA - DETAILS */
  // @todo usare webworker per non bloccare il main thread
  const [loadSessions, {
    data: detailsData,
  }] = useLazyQueryCached<{ res: TeamSessionsType }, TeamSessionsVars>(TEAMSESSION_ATHLETES_SESSIONS, {
    variables: {
      drill: currentDrill,
      fieldsLimit: 6,
      id: parseInt(sessionId, 10),
      templateId: parseInt(templateId, 10),
    },
    fetchPolicy: 'cache-first',
    onCompleted: (d) => {
      setStatus(STATUS_TEXT.PLAYERS_READY);
      setEnabledPlayers(d.res.athleteSessions.map((a) => a.id));
    },
    onError: (error) => {
      console.error('Error fetching athletes', error);
    },
  });

  /* ATH SESSIONS */
  const athSess = detailsData?.res.athleteSessions || [];
  const athSessionsNumRef = useRef(athSess.length);
  const maxSamples = getLoadingTimeFrame(athSessionsNumRef.current) * maxSamplesPerFrame;

  useEffect(() => {
    athSessionsNumRef.current = athSess.length;
  }, [athSess.length]);

  const getAthSessionsInCurrentDrill = () => data?.res?.athleteSessions
  .filter((as) => as.drills.relatedDrills.find(
    (rd) => rd.index === currDrillRef.current,
  )) || [];

  const athSessionsInCurrentDrill = useMemo(
    getAthSessionsInCurrentDrill,
    [data?.res?.athleteSessions, currDrillRef.current],
  );

  const getAthSessionIDsInCurrentDrill = () => getAthSessionsInCurrentDrill().map((as) => as.id);

  const athSessionIDsInCurrentDrill = useMemo(
    getAthSessionIDsInCurrentDrill,
    [athSessionsInCurrentDrill],
  );

  const athInCurrentDrillRef = useRef<{
    athSessionsInCurrentDrill: AthleteSessionType[],
    athSessionIDsInCurrentDrill: string[],
  }>({
    athSessionIDsInCurrentDrill: [],
    athSessionsInCurrentDrill: [],
  });

  useEffect(() => {
    athInCurrentDrillRef.current = {
      athSessionIDsInCurrentDrill,
      athSessionsInCurrentDrill,
    };
  }, [athSessionsInCurrentDrill, athSessionIDsInCurrentDrill]);

  /* GROUND */
  const groundDetails = data?.res?.athleteSessions && data.res.athleteSessions.length > 0
    && data.res.athleteSessions[0] && data.res.athleteSessions[0].track?.ground;
  const isGPSGround = ground?.groundCoordsType !== 'LOCAL';
  const currentGround = !isGPSGround ? ground : ground || sessionGround;
  const teamColors = useMemo(() => getMarkersColor(currentGround?.groundSurfaceColor || DEFAULT_GROUND_COLOR), []);

  const drillTimes: DrillTimesType = useMemo(() => {
    const times = {};

    marks.forEach((drill) => {
      const { index, value } = drill;
      // @todo verificare null
      if (index) {
        if (!times[index]) {
          times[index] = { start: value, end: value };
        } else {
          times[index].start = Math.min(times[index].start, value);
          times[index].end = Math.max(times[index].end, value);
        }
      }
    });

    return Object.values(times);
  }, [marks]);

  const loadPlayersData = ({ time, athleteSessionIds }: {time: number, athleteSessionIds: string[]}) => {
    if (
      getJwtToken()
      && (!endSessionTimestampRef || endSessionTimestampRef.current === 0 || time < endSessionTimestampRef.current)
      // && (loadingPlayers.length === 0 || loadingTypeRef.current === LOADING_TYPE.BG)
    ) {
      lastLoadedTimestampRef.current = time;
      setLoadingInterval([time, time + getLoadingTimeFrame(athSessionsNumRef.current) * 1000]);
      setLoadingPlayers(athleteSessionIds);

      webplayerWorkerNew.postMessage({
        action: 'loadMultiplePathData',
        athleteSessionIds,
        drill: currentDrill,
        jwtToken: getJwtToken(),
        maxSamples,
        start: time,
        end: time + getLoadingTimeFrame(athSessionsNumRef.current) * 1000, // 5 minuti
        computeData: {
          currentGround,
          ground,
          groundDetails,
          defaultGround,
        },
      });
    } else if (idleLoadingIntervalRef?.current) {
      // stiamo tentando di caricare oltre le fine della sessione, stop idle
      clearInterval(idleLoadingIntervalRef.current);
      idleLoadingIntervalRef.current = null;
    }
  };

  const loadAthSessionData = useCallback(async () => {
    if (!detailsData?.res?.athleteSessions || !currentGround) {
      return;
    }

    setStatus(STATUS_TEXT.PLAYERS_LOADED);
    loadingTypeRef.current = LOADING_TYPE.FG;

    if (!sessionLoading && athSess.length > 0) {
      setLoadingPlayers(athSessionIDsInCurrentDrill);

      const timezoneOffset = data?.res.startTimestamp
        ? -new Date(data?.res.startTimestamp).getTimezoneOffset() * 60000
        : 0;
      const startTime = currentTime || (data?.res.startTimestamp
        ? (new Date(data?.res.startTimestamp)).getTime() + timezoneOffset
        : 0);

      loadPlayersData({ time: startTime, athleteSessionIds: athSessionIDsInCurrentDrill });
    }

    setStatus(STATUS_TEXT.PLAYERS_READY);
  }, [
    athSessionIDsInCurrentDrill,
    detailsData?.res.athleteSessions,
    data?.res.athleteSessions,
    ground?.groundCoordsType,
    defaultGround,
    loadPlayersData,
    timestamps.length,
    currentDrill,
    data?.res?.endTimestamp,
    data?.res?.startTimestamp,
    sessionLoading,
  ]);

  const {
    data: dataGrounds,
  } = useQueryCached<ClubTypeRes, ClubTypeVars>(CLUB, {
    variables: {
      id: teamId,
    },
    ...CACHE_AND_NETWORK,
  });

  const groundSet = dataGrounds?.res?.club?.groundSet || [];

  const groundOptions = useMemo<OptionType[]>(() => groundSet?.map((g) => ({
    id: String(g.id),
    value: String(g.name),
    label: String(g.name),
  })), [groundSet]);

  const isPlayingDataReady = (time: number | undefined) => getAthSessionIDsInCurrentDrill().every((playerId) => {
    if (!time) {
      console.lg('isPlayingDataReady', time)
      return false;
    }
    const isReady = !loadingPlayers.includes(playerId);
    const hasData = !!series[playerId]?.pathData[time];

    return isReady && hasData;
  })

  const playersTeamsContextValue = useMemo(() => ({
    playersTeams, setPlayersTeams,
  }), [playersTeams, setPlayersTeams]);

  const startPlayer = useCallback((text?: string) => {
    fpsCtrlRef.current.start();
    setCurrentPlayerStatus(true);
  }, [fpsCtrlRef.current, currentPlayerStatus]);

  const stopPlayer = useCallback((text?: string) => {
    fpsCtrlRef.current.pause();
    setCurrentPlayerStatus(false);
  }, [fpsCtrlRef.current, currentPlayerStatus]);

  const handlePlayClick = (forcePlay = false, dontSetLastRequestedTimestamp = false) => {
    if (!dontSetLastRequestedTimestamp) {
      lastRequestedTimestampRef.current = cursorValue.cursor;
    }
    if (
      forcePlay || currentPlayerStatus === PLAYER_STATUS.STOP
    ) {
      // @todo va gestitia la situazione particolare dove ho dei player per il drill, ma non ci sono dati per i punti
      // nemmeno dopo i tentativi di caricamento
      //if (isPlayingDataReady(cursorValue.cursor)) {
        fpsCtrlRef.current.start();
        setCurrentPlayerStatus(true);
      /*} else {
        console.log('handlePlayClick - 4')
      }*/

    } else {
      fpsCtrlRef.current.pause();
      setCurrentPlayerStatus(false);
    }
  };

  const handleChange = (_: ChangeEvent<{}> | null, time: number, background = false, isDrag = false) => {
    if (endSessionTimestampRef?.current && time > endSessionTimestampRef.current) {
      return;
    }
    currDrillRef.current = getCurrentDrill();

    if (timestamps[time]) {
      // ho il time nei timestamp, posiziono idx
      setCurrentTime(time);
      lastRequestedTimestampRef.current = time;
    } else {
      // non ho time, ne cerco uno vicino
      const nearIdx = findNearTimestampIndex(time);
      if (!background && nearIdx > -1) {
        // c'è uno vicino
        setCurrentTime(time);
        lastRequestedTimestampRef.current = time;
      } else {
        // non c'è ne time ne uno vicino, mancano dati, richiedo nuovi dati
        if (!background) {
          setCurrentTime(time);
          lastRequestedTimestampRef.current = time;
        } else {
        }

        if (!isDrag) {
          const aths = athInCurrentDrillRef.current.athSessionIDsInCurrentDrill;
          loadingTypeRef.current = background ? LOADING_TYPE.BG : LOADING_TYPE.FG;

          if (!background) {
            lastRequestedTimestampRef.current = time;
            loadPlayersData({
              time,
              athleteSessionIds: aths,
            });
          } else if (lastLoadedTimestampRef.current) {
            loadPlayersData({
              time: lastLoadedTimestampRef.current + getLoadingTimeFrame(athSessionsNumRef.current) * 1000,
              athleteSessionIds: aths,
            });
          } else {
          }
        } else {
        }
      }
    }
  };

  const goToNextDrill = () => {
    const wasPlaying = currentPlayerStatus;
    if (wasPlaying) {
      handlePlayClick(false, true);
    }

    const nextDrill = marks.filter((m) => m.index === currDrillRef.current);
    if (nextDrill.length) {
      handleChange(null, nextDrill[0].value);
    }

    if (wasPlaying) {
      handlePlayClick(true, true);
    }
  };

  const goToPrevDrill = () => {
    const wasPlaying = currentPlayerStatus;
    if (wasPlaying) {
      handlePlayClick(false, true);
    }
    if (currDrillRef.current > 1) {
      currDrillRef.current -= 1;
    }
    const prevDrill = marks.filter((m) => m.index === currDrillRef.current);
    if (prevDrill.length) {
      handleChange(null, prevDrill[0].value);
    }

    if (wasPlaying) {
      handlePlayClick(true, true);
    }
  };

  const handlePlayerClick = useCallback((playerID: string) => {
    if (!currentActivePlayer) {
      setCurrentActivePlayer(playerID);
    } else if (currentActivePlayer && currentActivePlayer === playerID) {
      setCurrentActivePlayer(null);
    } else {
      const existingLink = Array.from(playerLinks)
        .find((link) => link[0] === currentActivePlayer && link[1] === playerID
        || link[0] === playerID && link[1] === currentActivePlayer);

      if (!existingLink) {
        const newPlayerLinks = new Set(playerLinks);
        newPlayerLinks.add([currentActivePlayer, playerID]);
        setPlayerLinks(newPlayerLinks);
      }

      setCurrentActivePlayer(null);
    }
  }, [currentActivePlayer, playerLinks]);

  const handlePlayerEnter = (playerID: string) => {
    setCurrentHoverPlayer(playerID);
  };

  const handlePlayerLeave = () => {
    setCurrentHoverPlayer(null);
  };

  const playersDetails: PlayersDetails = useMemo(() => (detailsData?.res?.athleteSessions
    ? detailsData?.res?.athleteSessions.reduce((acc, curr) => {
      if (curr.athlete?.id) {
        acc[curr.id] = {
          id: curr.athlete?.id,
          name: curr.athlete?.name,
          number: curr.athlete?.playerSet.find((ps) => ps.team?.id && ps.team?.id === teamId)?.number || null,
          shortName: curr.athlete?.shortName,
        };
      }

      return acc;
    }, {})
    : {}), [detailsData?.res?.athleteSessions, teamId]);

  const handleRemoveLink = (linkIdx: number) => {
    // eslint-disable-next-line no-restricted-globals
    const newPlayerLinks = Array.from(playerLinks);
    newPlayerLinks.splice(linkIdx, 1);
    setPlayerLinks(new Set(newPlayerLinks));
  };

  const handleFullscreenClick = async () => {
    if (document.fullscreenElement) {
      await exitFullscreen();
    } else if (playerRef.current) {
      try {
        await enterFullscreen(playerRef.current);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const defaultPlayersColor = !isGPSGround
    ? ground?.groundSurfaceColor ? getPairedGroundColor(ground?.groundSurfaceColor) : PLAYER_MARKER_COLOR
    : sessionGround?.groundSurfaceColor
      ? getPairedGroundColor(sessionGround?.groundSurfaceColor)
      : PLAYER_MARKER_COLOR;

  const indexedMarks = useMemo(() => (marks || []).map((m) => ({
    ...m,
    idx: findNearTimestampIndex(m.value),
  })), [marks, findNearTimestampIndex]);

  const isLoadingDataRef = useRef(false);
  const isLoadingData = currentPlayerStatus === PLAYER_STATUS.STOP && loadingPlayers.length > 0;

  useEffect(() => {
    isLoadingDataRef.current = isLoadingData;
  }, [isLoadingData]);

  const userUnits = useMemo(() => me.userunits, [me.userunits]);

  const playersExtraData = useMemo<PlayersExtraData>(() => enabledPlayers.reduce((acc, pl) => {
    acc[pl] = cursorValue.cursor && series[pl] && series[pl].pathData[cursorValue.cursor] ? {
      s: series[pl].pathData[cursorValue.cursor].s,
      hr: series[pl].pathData[cursorValue.cursor].hr,
      h: series[pl].pathData[cursorValue.cursor].h,
    } : { s: null, hr: null };

    return acc;
  }, {}), [enabledPlayers, series, cursorValue.cursor]);

  const searchNextEmptyPoint = useCallback((time: number) => {
    const interval = intervals.find((i) => time >= i[0] && time <= i[1]);

    if (!interval) {
      return time;
    }

    return interval[1];
  }, [intervals]);

  const lastPlayerFPS = useRef(FPS);

  useEffect(() => {
    if (timestamps.length > 0 && !fpsCtrlRef.current || playerFPS !== lastPlayerFPS.current) {
      /*
      const wasPlaying = currentPlayerStatus;

      if (wasPlaying) {
        stopPlayer('timestamps');
      }
      */

      if (fpsCtrlRef.current) {
        fpsCtrlRef.current.pause();
        fpsCtrlRef.current = null;
      }

      console.log('Init FPS controller')
      fpsCtrlRef.current = new FpsCtrl(
        playerFPS,
        (timeData: { frame: number }) => {
          const { frame } = timeData;

          setCurrentTime((prevState) => (lastRequestedTimestampRef.current || prevState) + frame * (1 / playerFPS) * 1000);
        },
        playerSpeed,
      );

      lastPlayerFPS.current = playerFPS;

      /*if (wasPlaying) {
        startPlayer('timestamps');
      }*/
    }
  }, [timestamps, currentPlayerStatus, playerFPS]);

  // gestione messaggi dal worker
  useEffect(() => {
    webplayerWorkerNew.onmessage = (e: MessageEvent<WebPlayerWorkerResponse>) => {
      if (e.data?.type === 'error') {
        if (e.data.errorData?.id) {
          // rimuovo il player da quelli da caricare
          const newLoadingPlayers = [...loadingPlayers];
          newLoadingPlayers.splice(newLoadingPlayers.indexOf(String(e.data.errorData?.id)), 1);
          setLoadingPlayers(newLoadingPlayers);
          return;
        }
      }

      // il worker risponde, è pronto
      if (e.data?.type === 'ready' && !workerIsReady) {
        console.log('%c - webplayerWorker ✔', 'color: green');
        if (workerCheckerRef.current) {
          clearInterval(workerCheckerRef.current);
        }
        setWorkerIsReady(true);
      }

      // dati non validi o lunghezza sessione nulla
      if (!e.data?.serie?.id || (!athSess || athSess.length === 0)) {
        return;
      }

      // dati dal messaggio del loader
      const {
        id,
        data: d,
        intervals: dataIntervals,
      } = e.data.serie;

      let tmpPath: {
        t: number,
        x: number | null,
        x_0?: number | null,
        y: number | null,
        y_0?: number | null,
        s: number | null,
        h: number | null,
        hr?: number | null,
      }[] = [];

      // ci sono dati
      if (d && d.length > 0) {
        // è un ground LPS
        if (ground?.groundCoordsType === 'LOCAL') {
          d.forEach((p) => {
            const newPoint = p[1] === null || p[2] === null
              ? {
                x: null,
                y: null,
                s: null,
                h: null,
                hr: null,
              }
              : {
                x: p[1],
                y: p[2],
                s: p[3],
                h: p[4],
                hr: p[5],
              };

            tmpPath.push({
              t: Math.trunc(p[0] * 1000),
              x: newPoint.x,
              y: newPoint.y,
              s: newPoint.s,
              h: newPoint.h,
              hr: newPoint.hr,
            });
          });
        } else {
          try {
            const {
              vertexALatitude,
              vertexALongitude,
              vertexBLatitude,
              vertexBLongitude,
              vertexCLatitude,
              vertexCLongitude,
            } = currentGround || {};

            // trasformo i dati GPS in coordinate locali in base al ground
            tmpPath = LPSFromGPSPath(
              d.map((p) => ({
                t: Math.trunc(p[0] * 1000),
                x: p[1],
                y: p[2],
                s: p[3],
                h: p[4],
                hr: p[5],
              })),
              { latitude: vertexALatitude || 0, longitude: vertexALongitude || 0 },
              { latitude: vertexBLatitude || 0, longitude: vertexBLongitude || 0 },
              { latitude: vertexCLatitude || 0, longitude: vertexCLongitude || 0 },
            );
          } catch (err) {
            console.log('Cannot compute LPS from GPS path', err, currentGround);
          }
        }
      } else {
        console.log('No path data', d);
      }

      // trasformo in oggetto per accesso O(1)
      const pathDataObj = tmpPath.reduce((acc, curr) => {
        acc[curr.t] = {
          x: curr.x || null,
          x_0: curr.x_0 || null,
          y: curr.y || null,
          y_0: curr.y_0 || null,
          s: curr.s || null,
          h: curr.h || null,
          hr: curr.hr || null,
        };
        return acc;
      }, {});

      // ci sono dati per i timestamp aggiuntivi
      if ((Object.keys(pathDataObj).length > 0)) {
        setTimestampsToAdd(Object.keys(pathDataObj).map((t) => parseInt(t, 10)).sort());
      }

      // aggiungo i dati alla serie
      setSeries((prevState) => ({
        ...prevState,
        [String(id)]: {
          pathData: pathDataObj,
        },
      }));

      // rimuovo il player da quelli da caricare
      const newLoadingPlayers = [...loadingPlayers];
      newLoadingPlayers.splice(newLoadingPlayers.indexOf(String(id)), 1);
      setLoadingPlayers(newLoadingPlayers);

      if (newLoadingPlayers.length === 0 && loadingTypeRef.current !== LOADING_TYPE.NONE) {
        loadingTypeRef.current = LOADING_TYPE.NONE;
      }

      setIntervals(mergeArraysUnique(dataIntervals, intervals));
    };
  }, [ground, groundDetails, athSess, intervals, loadingPlayers]);

  const getCurrentDrill = useCallback(() => {
    const cdIndex = drillTimes.findIndex((dt) => dt.start <= currentTime && dt.end >= currentTime);
    return cdIndex !== -1 ? cdIndex + 1 : 1;
  }, [currentTime, drillTimes])

  useEffect(() => {
    currDrillRef.current = getCurrentDrill();
  }, [currentTime, drillTimes]);

  useEffect(() => {
    // attendo che il worker sia pronto
    if (!workerIsReady && !workerCheckerRef.current) {
      workerCheckerRef.current = setInterval(() => {
        webplayerWorkerNew.postMessage({
          action: 'requireInit',
        });
      }, 500);
    }

    return () => {
      if (workerCheckerRef.current) {
        clearInterval(workerCheckerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (timestamps && timestamps.length > 0 && data?.res?.startTimestamp && data?.res?.endTimestamp) {
      currStepRef.current = 1 / playerFPS;
    }
  }, [timestamps, data?.res?.startTimestamp, data?.res?.endTimestamp]);

  const lastPlayerSpeedRef = useRef(1);

  useEffect(() => {
    if (fpsCtrlRef.current && lastPlayerSpeedRef.current !== playerSpeed) {
      currPlayerSpeedRef.current = playerSpeed;
      lastPlayerSpeedRef.current = playerSpeed;

      const wasPlaying = currentPlayerStatus;
      if (wasPlaying) {
        handlePlayClick(false, false);
      }

      fpsCtrlRef.current.setMultiplier(playerSpeed);

      if (wasPlaying) {
        handlePlayClick(true);
      }
    }
  }, [playerSpeed, currentPlayerStatus]);

  useEffect(() => {
    if (timestampsToAdd.length > 0) {
      setTimestamps((prevValue) => (Array.from(new Set([
        ...timestampsToAdd.sort(),
        ...prevValue,
      ]))));

      setTimestampsToAdd([]);

      if (
        currentPlayerStatus === PLAYER_STATUS.STOP
        && lastRequestedTimestampRef.current
      ) {
        setCurrentTime(lastRequestedTimestampRef.current);
      }
    }
  }, [timestampsToAdd, currentPlayerStatus]);

  useEffect(() => {
    if (workerIsReady && athSess.length > 0) {
      // primo caricamento
      loadAthSessionData();
    }
  }, [
    workerIsReady,
    detailsData,
    ground,
    currentDrill,
    athSess.length,
  ]);

  useEffect(() => {
    if (
      !ground
      && groundOptions.length > 0
      && groundSet.length > 0
      && detailsData
      && data
    ) {
      const selectedGround = groundSet.find((g) => g.id === groundOptions[0].id);

      if (selectedGround) {
        setGround(selectedGround);
      }
    }
  }, [groundOptions, ground, groundSet, detailsData, data]);

  useEffect(() => {
    const keysEventListener = (event: KeyboardEvent) => keyboardEventHandler(event, [
      { key: 'Space', callbackFn: handlePlayClick },
      { key: 'ArrowLeft', callbackFn: () => fpsCtrlRef.current.stepBackward(1) },
      { key: 'ArrowLeft', callbackFn: () => fpsCtrlRef.current.stepBackward(10), shiftKey: true },
      { key: 'ArrowRight', callbackFn: () => fpsCtrlRef.current.stepForward(1) },
      { key: 'ArrowRight', callbackFn: () => fpsCtrlRef.current.stepForward(10), shiftKey: true },
    ]);

    document.addEventListener(
      'keydown',
      keysEventListener,
      false,
    );
    return () => {
      document.removeEventListener('keydown', keysEventListener);
    };
  }, [currentPlayerStatus, handlePlayClick]); // @todo check dependencies

  useEffect(() => {
    // check if currentTime is near the end of an interval
    checkNearEndOfInterval(
      currentTime,
      intervals,
      (interval: PlayerDataInterval) => {
        if (
          loadingPlayers.length === 0
          && currentPlayerStatus === PLAYER_STATUS.PLAY
          && lastLoadedIntervalRef.current !== interval
        ) {
          lastLoadedIntervalRef.current = interval;
          loadPlayersData({
            time: interval[1],
            athleteSessionIds: athSessionIDsInCurrentDrill
          });
        }
      },
      30000 * playerSpeed,
    );
  }, [
    currentTime,
    currentPlayerStatus,
    intervals,
    endSessionTimestamp,
    athSessionIDsInCurrentDrill,
    loadPlayersData,
  ]);

  useEffect(() => {
    tickIntervalRef.current = setInterval(() => {
      setTickThrottle(!tickThrottle);
    }, 300);

    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoadingDataRef.current && prevPlayerIsLoadingRef.current) {
      if (window.playerIsReady) {
        console.log('[event] playerIsReady');
        window.playerIsReady({
          duration: (data
            ? currentDrill !== null && data
              ? data.res.drills.relatedDrills[currentDrill].totalTime.value
              : data.res.drills.wholeSession.totalTime.value
            : 0) * 1000,
        });
      }
    }

    prevPlayerIsLoadingRef.current = isLoadingDataRef.current;
  }, [isLoadingData]);

  useEffect(() => {
    loadingPlayersRef.current = loadingPlayers.length;
  }, [loadingPlayers.length]);

  useEffect(() => () => {
    clearInterval(idleLoadingIntervalRef.current);
    idleLoadingIntervalRef.current = null;
  }, []);

  // @todo verifica map in produzione

  // gestisco stato player durante la riproduzione
  useEffect(() => {
    if (
      endSessionTimestampRef?.current
      && currentTime > endSessionTimestampRef.current
    ) {
      console.log('[idle preload] stop END_SESSION');
      fpsCtrlRef.current.pause();
      setCurrentPlayerStatus(false);
    }

    if (currentPlayerStatus === PLAYER_STATUS.PLAY && isLoadingForeground) {
      console.log('[idle preload] stop NO_DATA')
      // sono in play e non ci sono dati, stop
      fpsCtrlRef.current.pause();
      setCurrentPlayerStatus(false);
      loadingTypeRef.current = LOADING_TYPE.FG;
    }
  }, [
    lastLoadedTimestampRef?.current, endSessionTimestamp, currentTime,
    isLoadingForeground, currentPlayerStatus, fpsCtrlRef.current,
  ]);

  const loaderIntervalRef = useRef<any>();

  const idleLoader = () => {
    if (
      !isLoadingDataRef.current
      && loadingTypeRef.current === LOADING_TYPE.NONE
      && lastLoadedTimestampRef?.current
    ) {
      console.log('[idle preload] ', lastLoadedTimestampRef.current);
      handleChange(null, searchNextEmptyPoint(lastLoadedTimestampRef.current), true);
    }
  }

  // gestisco il loading in idle
  useEffect(() => {
    // ritardo l'inizializzazione del loader per evitare che venga caricato prima del primo frame
    if (!loaderIntervalRef.current) {
      loaderIntervalRef.current = setInterval(
        idleLoader,
        20000,
      );
    }

    return () => {
      if (loaderIntervalRef.current) {
        clearTimeout(loaderIntervalRef.current);
      }
    };
  }, []);

  return (
    <ErrorGuard>
      <TourProvider steps={steps} scrollSmooth>
        <div className="tracks-player" ref={playerRef}>

          {
            !isFullscreen
            && (
              <WebPlayerHeader
                ground={ground}
                groundOptions={groundOptions}
                groundSet={groundSet}
                jerseyOrNumber={jerseyOrNumber}
                setGround={setGround}
                setJerseyOrNumber={setJerseyOrNumber}
                setTrailsEnabled={setTrailsEnabled}
                trailsEnabled={trailsEnabled}
                isSessionDataReady={!!data}
              />
            )
          }

          <Row className="tracks-player-container">
            <PlayersTeamsContext.Provider value={playersTeamsContextValue}>
              <Column xs={isFullscreen ? 12 : 8} className="tracks-player-column">
                <Box p={isFullscreen ? 0 : 2} style={{ paddingLeft: isFullscreen ? '0' : '10px' }}>
                  <CursorContext.Provider value={cursorValue}>
                    {
                      currentGround
                      && (
                        <Ground
                          currentActivePlayer={currentActivePlayer}
                          hoverPlayer={currentHoverPlayer}
                          defaultPlayersColor={defaultPlayersColor}
                          enabledPlayers={enabledPlayers}
                          ground={currentGround}
                          handlePlayerClick={handlePlayerClick}
                          handlePlayerEnter={handlePlayerEnter}
                          handlePlayerLeave={handlePlayerLeave}
                          handleRemoveLink={handleRemoveLink}
                          infoPlayers={infoPlayers}
                          isLoadingData={isLoadingForeground}
                          jerseyOrNumber={jerseyOrNumber}
                          playerLinks={playerLinks}
                          playersColors={playersColors}
                          playersDetails={playersDetails}
                          series={series}
                          showVertices={showVertices}
                          teamColors={teamColors}
                          trailsEnabled={trailsEnabled}
                          userUnits={userUnits}
                        />
                      )
                    }

                    <PlayerControls
                      currDrillRef={currDrillRef}
                      currentDrill={currentDrill}
                      currentTime={currentTime}
                      drillTimes={drillTimes}
                      enabled={!isLoadingForeground} // !isLoadingForeground
                      endTimestamp={data?.res?.endTimestamp}
                      goToNextDrill={goToNextDrill}
                      goToPrevDrill={goToPrevDrill}
                      handleChange={handleChange}
                      handleFullscreenClick={handleFullscreenClick}
                      handlePlayClick={() => handlePlayClick()}
                      intervals={intervals}
                      loadingInterval={loadingInterval}
                      marks={indexedMarks}
                      playerSpeed={playerSpeed}
                      playerStatus={currentPlayerStatus ? 1 : 0} // @todo usare stati boolean
                      setPlayerSpeed={setPlayerSpeed}
                      startTimestamp={data?.res?.startTimestamp}
                      timeFormat={timeFormat}
                      valueLabelFormat={valueLabelFormat}
                      timestamps={timestamps}
                    />
                  </CursorContext.Provider>
                </Box>
              </Column>

              {
                !isFullscreen
                && (
                  <Column xs={4}>
                    <PlayersTable
                      athleteSessions={detailsData?.res?.athleteSessions || []}
                      activeAthSessions={athSessionIDsInCurrentDrill}
                      enabledPlayers={enabledPlayers}
                      loadingPlayers={isLoadingForeground ? loadingPlayers : []} // isLoadingForeground ? loadingPlayers : []
                      groundSurfaceColor={ground?.groundSurfaceColor || DEFAULT_GROUND_COLOR}
                      infoPlayers={infoPlayers}
                      jerseyOrNumber={jerseyOrNumber}
                      playersColors={playersColors}
                      playersExtraData={playersExtraData}
                      playersDetails={playersDetails}
                      setEnabledPlayers={setEnabledPlayers}
                      setPlayersColors={setPlayersColors}
                      setInfoPlayers={setInfoPlayers}
                      status={status}
                      teamColors={teamColors}
                      userUnits={userUnits}
                    />

                    <StaffOnlyWrapper>
                      <TourGuide />
                      <select
                        onChange={(e) => setPlayerFPS(parseInt(e.currentTarget.value, 10))}
                        defaultValue={playerFPS}
                      >
                        <option>2</option>
                        <option>3</option>
                        <option>5</option>
                        <option>10</option>
                        <option>15</option>
                      </select>
                    </StaffOnlyWrapper>
                  </Column>
                )
              }

            </PlayersTeamsContext.Provider>
          </Row>

        </div>
      </TourProvider>
    </ErrorGuard>
  );
};

export default withMe(WebPlayer);
